import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowLeft,
	ExternalLink,
	Mail,
	Sparkles,
} from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ActivityTimeline } from "@/components/crm/activity-timeline";
import { CrmLoadError, CrmRoleNotice } from "@/components/crm/crm-page";
import { EmailComposeDialog } from "@/components/crm/email-compose-dialog";
import { InlineField, InlineRow } from "@/components/crm/inline-field";
import { LostReasonDialog } from "@/components/crm/lost-reason-dialog";
import { OwnerSelect } from "@/components/crm/owner-select";
import { StageBadge } from "@/components/crm/stage-badge";
import { useStageMove } from "@/components/crm/stage-move";
import { TasksPanel } from "@/components/crm/task-list";
import { UpcomingMeetings } from "@/components/crm/upcoming-meetings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { useRegional } from "@/hooks/use-regional";
import { CRM_STAGES } from "@/lib/crm/constants";
import { contactName, decodeCrmError, formatNumber } from "@/lib/crm/format";
import { CRM_STALE_TIME, invalidateCrmData } from "@/lib/crm/queries";
import { formatDate, formatDateTime } from "@/lib/crm/regional";
import type { CrmStage } from "@/lib/crm/types";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/opportunities/$opportunityId")({
	component: DealPage,
});

const NO_CONTACT = "__none";

function DealPage(): ReactElement {
	const { opportunityId } = Route.useParams();
	const { t, i18n } = useTranslation();
	const regional = useRegional();
	const queryClient = useQueryClient();
	const { canManage } = useCrmPermissions();
	const { profile } = useAccessProfile();
	const { requestMove, dialogs } = useStageMove();
	const [acceptLostOpen, setAcceptLostOpen] = useState(false);
	const [composeOpen, setComposeOpen] = useState(false);

	const {
		data: deal,
		isLoading,
		isError,
	} = useQuery({
		...orpc.admin.crm.opportunities.get.queryOptions({
			input: { id: opportunityId },
		}),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});
	const { data: contactsData } = useQuery({
		...orpc.admin.crm.contacts.list.queryOptions({
			input: {
				organizationId: deal?.organizationId,
				limit: 100,
				offset: 0,
			},
		}),
		enabled: !!deal,
		staleTime: CRM_STALE_TIME,
	});

	const update = useMutation(
		orpc.admin.crm.opportunities.update.mutationOptions(),
	);
	const accept = useMutation(
		orpc.admin.crm.opportunities.acceptSuggestion.mutationOptions(),
	);
	const dismiss = useMutation(
		orpc.admin.crm.opportunities.dismissSuggestion.mutationOptions(),
	);
	const keepOpen = useMutation(
		orpc.admin.crm.opportunities.keepOpen.mutationOptions(),
	);

	if (!canManage) return <CrmRoleNotice />;
	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-1/2" />
				<Skeleton className="h-40" />
			</div>
		);
	}
	if (isError || !deal) return <CrmLoadError />;

	const run = async (action: () => Promise<unknown>): Promise<void> => {
		try {
			await action();
			await invalidateCrmData(queryClient);
		} catch (error) {
			toast.error(
				decodeCrmError(error).message ?? t("admin:crm.errors.generic"),
			);
			throw error;
		}
	};
	const patch = (fields: Parameters<typeof update.mutateAsync>[0]["patch"]) =>
		run(() => update.mutateAsync({ id: deal.id, patch: fields }));
	const safe = (promise: Promise<void>): void => {
		promise.catch(() => undefined);
	};

	const contacts = contactsData?.contacts ?? [];
	const person = contactName({
		firstName: deal.contactFirstName,
		lastName: deal.contactLastName,
		email: deal.contactEmail,
	});
	const reasonLabel = (code: string | null): string =>
		code ? t(`admin:crm.reasons.${code}`, { defaultValue: code }) : "";

	return (
		<div className="space-y-4">
			<Link
				to="/crm/opportunities"
				className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:underline"
			>
				<ArrowLeft className="h-4 w-4" />
				{t("admin:crm.pipeline.title")}
			</Link>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0 space-y-1">
					<h1 className="font-bold text-3xl">{deal.title}</h1>
					<p className="text-muted-foreground">
						<Link
							to="/crm/organizations/$organizationId"
							params={{ organizationId: deal.organizationId }}
							className="hover:underline"
						>
							{deal.organizationName}
						</Link>
						{person && deal.contactId && (
							<>
								{" · "}
								<Link
									to="/crm/contacts/$contactId"
									params={{ contactId: deal.contactId }}
									className="hover:underline"
								>
									{person}
								</Link>
							</>
						)}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{deal.contactEmail && deal.contactId && (
						<>
							<Button
								variant="outline"
								className="bg-card dark:bg-card"
								onClick={() => setComposeOpen(true)}
							>
								<Mail className="h-4 w-4" />
								{t("admin:crm.compose.title")}
							</Button>
							<EmailComposeDialog
								open={composeOpen}
								onOpenChange={setComposeOpen}
								to={[deal.contactEmail]}
								links={[
									{ entityType: "opportunity", entityId: deal.id },
									{ entityType: "contact", entityId: deal.contactId },
								]}
							/>
						</>
					)}
					<StageBadge stage={deal.stage} />
					<Select
						value={deal.stage}
						onValueChange={(stage) => requestMove(deal, stage as CrmStage)}
					>
						<SelectTrigger
							className="w-44"
							aria-label={t("admin:crm.deal.stage")}
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{CRM_STAGES.map((stage) => (
								<SelectItem key={stage} value={stage}>
									{t(`admin:crm.stage.${stage}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{deal.suggestedStage && (
				<div
					className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm dark:bg-emerald-950"
					data-testid="deal-suggestion-banner"
				>
					<Sparkles className="h-4 w-4 text-emerald-600" />
					<span className="flex-1">
						{t("admin:crm.deal.suggestion_banner", {
							stage: t(`admin:crm.stage.${deal.suggestedStage}`),
						})}
						{deal.suggestionReason && ` ${reasonLabel(deal.suggestionReason)}`}
					</span>
					<Button
						size="sm"
						disabled={accept.isPending}
						onClick={() => {
							if (deal.suggestedStage === "closed_lost") {
								setAcceptLostOpen(true);
							} else {
								safe(run(() => accept.mutateAsync({ id: deal.id })));
							}
						}}
					>
						{t("admin:crm.deal.accept_suggestion")}
					</Button>
					<Button
						size="sm"
						variant="outline"
						disabled={dismiss.isPending}
						onClick={() =>
							safe(run(() => dismiss.mutateAsync({ id: deal.id })))
						}
					>
						{t("admin:crm.deal.dismiss_suggestion")}
					</Button>
				</div>
			)}

			{deal.needsActionSince && (
				<div
					className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:bg-amber-950"
					data-testid="deal-needs-action-banner"
				>
					<AlertTriangle className="h-4 w-4 text-amber-600" />
					<span className="flex-1">
						{t("admin:crm.deal.needs_action_banner", {
							date: formatDate(deal.needsActionSince, regional),
						})}
						{deal.needsActionReason &&
							` ${reasonLabel(deal.needsActionReason)}`}
					</span>
					<Button
						size="sm"
						variant="outline"
						disabled={keepOpen.isPending}
						onClick={() =>
							safe(run(() => keepOpen.mutateAsync({ id: deal.id })))
						}
					>
						{t("admin:crm.deal.keep_open")}
					</Button>
					<Button
						size="sm"
						variant="destructive"
						onClick={() => requestMove(deal, "closed_lost")}
					>
						{t("admin:crm.deal.mark_lost")}
					</Button>
				</div>
			)}

			<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
				<div className="space-y-4">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base">
								{t("admin:crm.deal.details")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<dl>
								<InlineField
									label={t("admin:crm.deal.name")}
									value={deal.title}
									editable
									onSave={(value) =>
										patch({ title: value.trim() || deal.title })
									}
								/>
								<InlineField
									label={t("admin:crm.deal.description")}
									type="textarea"
									value={deal.description ?? ""}
									editable
									onSave={(value) =>
										patch({ description: value.trim() || null })
									}
								/>
								<InlineField
									label={t("admin:crm.deal.potential_users")}
									type="number"
									value={deal.potentialUsers?.toString() ?? ""}
									display={
										deal.potentialUsers !== null
											? formatNumber(deal.potentialUsers, i18n.language)
											: undefined
									}
									editable
									onSave={(value) => {
										const users = value.trim() ? Number(value) : null;
										if (
											users !== null &&
											(!Number.isInteger(users) || users < 1)
										) {
											toast.error(t("admin:crm.deal_form.error_users"));
											return Promise.reject(new Error("invalid"));
										}
										return patch({ potentialUsers: users });
									}}
								/>
								<InlineField
									label={t("admin:crm.deal.expected_close")}
									type="date"
									dateShortcuts="close"
									value={deal.expectedCloseDate ?? ""}
									display={
										deal.expectedCloseDate
											? formatDate(deal.expectedCloseDate, regional)
											: undefined
									}
									editable
									onSave={(value) =>
										patch({ expectedCloseDate: value || null })
									}
								/>
								<InlineRow label={t("admin:crm.deal.contact")}>
									<Select
										value={deal.contactId ?? NO_CONTACT}
										onValueChange={(value) =>
											safe(
												patch({
													contactId: value === NO_CONTACT ? null : value,
												}),
											)
										}
									>
										<SelectTrigger
											className="h-8"
											aria-label={t("admin:crm.deal.contact")}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_CONTACT}>
												{t("admin:crm.deal_form.no_contact")}
											</SelectItem>
											{deal.contactId &&
												!contacts.some(
													(contact) => contact.id === deal.contactId,
												) && (
													<SelectItem value={deal.contactId}>
														{person ?? deal.contactId}
													</SelectItem>
												)}
											{contacts.map((contact) => (
												<SelectItem key={contact.id} value={contact.id}>
													{contactName(contact) ?? contact.id}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</InlineRow>
								<InlineRow label={t("admin:crm.common.owner")}>
									<OwnerSelect
										className="h-8"
										value={deal.ownerId}
										onChange={(value) =>
											safe(patch({ ownerId: value ?? null }))
										}
									/>
								</InlineRow>
								{deal.actualCloseDate && (
									<InlineRow label={t("admin:crm.deal.closed_on")}>
										{formatDate(deal.actualCloseDate, regional)}
									</InlineRow>
								)}
								{deal.lostReason && (
									<InlineRow label={t("admin:crm.lost.reason")}>
										{t(`admin:crm.lost_reason.${deal.lostReason}`)}
									</InlineRow>
								)}
								{deal.stage === "closed_lost" && (
									<InlineField
										label={t("admin:crm.deal.lost_comment")}
										type="textarea"
										value={deal.lostComment ?? ""}
										editable
										onSave={(value) =>
											patch({ lostComment: value.trim() || null })
										}
									/>
								)}
								<InlineRow label={t("admin:crm.common.created")}>
									{formatDateTime(deal.createdAt, regional)}
								</InlineRow>
								{deal.platformOrganizationId && (
									<InlineRow label={t("admin:crm.deal.platform_org")}>
										{profile?.canViewSales ? (
											<Link
												to="/sales/$organizationId"
												params={{ organizationId: deal.platformOrganizationId }}
												className="inline-flex items-center gap-1 hover:underline"
											>
												{t("admin:crm.deal.view_account")}
												<ExternalLink className="h-3.5 w-3.5" />
											</Link>
										) : (
											<span className="text-muted-foreground">
												{t("admin:crm.deal.linked_account")}
											</span>
										)}
									</InlineRow>
								)}
							</dl>
						</CardContent>
					</Card>
					<UpcomingMeetings
						entity={{ entityType: "opportunity", entityId: deal.id }}
						bookFor={
							deal.contactId
								? { contactIds: [deal.contactId], opportunityId: deal.id }
								: undefined
						}
					/>
					<TasksPanel
						parent={{ entityType: "opportunity", entityId: deal.id }}
						canManage={canManage}
					/>
				</div>
				<ActivityTimeline
					entity={{ entityType: "opportunity", entityId: deal.id }}
					canManage={canManage}
				/>
			</div>
			{dialogs}
			<LostReasonDialog
				open={acceptLostOpen}
				onOpenChange={setAcceptLostOpen}
				dealTitle={deal.title}
				pending={accept.isPending}
				onConfirm={({ lostReason, lostComment }) => {
					setAcceptLostOpen(false);
					safe(
						run(() =>
							accept.mutateAsync({ id: deal.id, lostReason, lostComment }),
						),
					);
				}}
			/>
		</div>
	);
}
